import { toUserMessage } from '../../lib/errors.js';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonNote,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import {
  cameraOutline,
  closeOutline,
  scanOutline,
  trashOutline,
} from 'ionicons/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { focusInput } from '../forms.js';
import {
  ALLERGEN_LABELS_IT,
  type Allergen,
  FOOD_CATEGORIES,
  STORAGE_TYPE_TEMP,
  suggestAllergens,
} from '@trackeat/shared';
import { getCaptureProvider, type CaptureSource } from '../../lib/adapters/camera/index.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { canCreateFood, canCreateQuickLabelFood } from '../../lib/auth/capabilities.js';
import { useCelebrate } from '../CelebrationToast.js';
import { UomSelect } from '../UomSelect.js';
import { useEnqueueMaster, useMasters } from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { TechSheetPicker } from '../TechSheetPicker.js';
import { techSheetFromSaved, commitTechSheet, EMPTY_TECH_SHEET, type TechSheetState } from '../../lib/documents/techSheet.js';

// Forma minima del Food usata da questo editor. Volutamente locale e non
// importata da @trackeat/shared/FoodSchema perché diversi consumatori (warehouse,
// recipes, labels) leggono i food da TanStack Query con tipi più rilassati.
export interface FoodEditorValue {
  id: string;
  name: string;
  // T-419: nome corto per la stampa in etichetta (fallback a name).
  label_alias?: string | null;
  default_uom: string;
  category: string | null;
  storage_type?: string;
  target_temp_c_min?: number | null;
  target_temp_c_max?: number | null;
  shelf_life_days_opened?: number | null;
  shelf_life_hours_thawed?: number | null;
  barcode?: string | null;
  photo_file_id?: string | null;
  // T-459: path bucket tech-sheets della scheda tecnica (PDF). null se assente.
  data_sheet_file_id?: string | null;
  // Per i foods di categoria detersivi il documento canonico è la SDS.
  safety_sheet_file_id?: string | null;
  allergens?: string[];
  version?: number;
}

interface RecipeForCount {
  id: string;
  ingredients?: Array<{ food_id: string }>;
  archived_at?: string | null;
  // T-441: le varianti dieta (ricette figlie) copiano gli ingredienti della base
  // → escluse dal conteggio "usato in N ricette" per non contarle due volte.
  variant_of_recipe_id?: string | null;
}

export interface FoodEditorModalProps {
  isOpen: boolean;
  /** Se presente → modalità "Modifica". Se assente → modalità "Nuovo". */
  initial?: FoodEditorValue;
  /** Pre-compila il nome quando l'editor è aperto da una search vuota. */
  initialName?: string;
  onDismiss: () => void;
  /** Restituisce l'oggetto salvato (con id) al chiamante. */
  onSaved?: (saved: FoodEditorValue) => void;
  /**
   * Solo modalità edit: se passato, mostra il pulsante "Archivia".
   * La conferma IonAlert vive nella pagina padre (è cross-entity).
   */
  onRequestArchive?: (food: { id: string; name: string; version: number }) => void;
  /**
   * Impedisce di cambiare la UdM canonica con un semplice update del master
   * quando esiste giacenza. Il chiamante deve offrire il rebase atomico che
   * converte anche quantità e valore dei lotti.
   */
  lockDefaultUom?: boolean;
  /** Registra la provenienza della creazione operativa senza ricetta. */
  creationSource?: 'quick_label';
  /**
   * Apre il cutover UdM atomico. Deve restare disponibile anche quando lo
   * storico esiste ma la giacenza fisica è già a zero.
   */
  onRequestUomRebase?: (foodId: string) => void;
  /**
   * T-159: modalità "prodotto pulizia/detersivo". Pre-imposta categoria
   * `detersivi` + conservazione `secco`, nasconde temperature e allergeni
   * (non pertinenti ai detergenti). Crea comunque un `foods` → fonte unica
   * dei prodotti di pulizia, niente più tabella separata `cleaning_products`.
   */
  detergentMode?: boolean;
}

interface InternalFormState {
  id: string;
  name: string;
  label_alias: string;
  default_uom: string;
  category: string;
  storage_type: string;
  target_temp_c_min: string;
  target_temp_c_max: string;
  shelf_life_days_opened: string;
  shelf_life_hours_thawed: string;
  barcode: string;
  photo_b64: string | null;
  photo_path: string | null;
  // T-459: stato scheda tecnica (PDF), controllato dal parent come la foto.
  techSheet: TechSheetState;
  allergens: string[];
  allergens_user_edited: boolean;
  version: number;
}

type FoodEditorStep = 'identity' | 'safety' | 'details';

function buildInitialState(initial: FoodEditorValue | undefined, initialName: string | undefined, detergentMode = false): InternalFormState {
  if (initial) {
    const st = initial.storage_type ?? 'fresco';
    const preset = STORAGE_TYPE_TEMP[st];
    // Modalità edit: parto dagli allergeni salvati. Se sono vuoti, suggerisco
    // dalla categoria/nome (utente vede già qualche tag pronto da confermare).
    const existing = (initial.allergens ?? []) as string[];
    const initialAllergens = existing.length > 0
      ? existing
      : suggestAllergens(initial.name, initial.category);
    return {
      id: initial.id,
      name: initial.name,
      label_alias: initial.label_alias ?? '',
      default_uom: initial.default_uom ?? 'kg',
      category: initial.category ?? 'altro',
      storage_type: st,
      target_temp_c_min: initial.target_temp_c_min == null
        ? (preset ? String(preset.min) : '')
        : String(initial.target_temp_c_min),
      target_temp_c_max: initial.target_temp_c_max == null
        ? (preset ? String(preset.max) : '')
        : String(initial.target_temp_c_max),
      shelf_life_days_opened: initial.shelf_life_days_opened == null
        ? ''
        : String(initial.shelf_life_days_opened),
      shelf_life_hours_thawed: initial.shelf_life_hours_thawed == null
        ? ''
        : String(initial.shelf_life_hours_thawed),
      barcode: initial.barcode ?? '',
      photo_b64: null,
      photo_path: initial.photo_file_id ?? null,
      techSheet: techSheetFromSaved(
        detergentMode
          ? initial.safety_sheet_file_id ?? null
          : initial.data_sheet_file_id ?? null,
      ),
      allergens: initialAllergens,
      // Considera "già modificato" solo se aveva allergeni salvati: così
      // i food legacy vuoti vengono auto-suggeriti dalla categoria.
      allergens_user_edited: existing.length > 0,
      version: initial.version ?? 0,
    };
  }
  const cat = detergentMode ? 'detersivi' : 'altro';
  const storage = detergentMode ? 'secco' : 'fresco';
  const preset = STORAGE_TYPE_TEMP[storage] ?? STORAGE_TYPE_TEMP.fresco;
  const name = initialName ?? '';
  return {
    id: '',
    name,
    label_alias: '',
    default_uom: detergentMode ? 'pz' : 'kg',
    category: cat,
    storage_type: storage,
    target_temp_c_min: String(preset.min),
    target_temp_c_max: String(preset.max),
    shelf_life_days_opened: '',
    shelf_life_hours_thawed: '',
    barcode: '',
    photo_b64: null,
    photo_path: null,
    techSheet: EMPTY_TECH_SHEET,
    allergens: detergentMode ? [] : (name ? suggestAllergens(name, 'altro') : []),
    allergens_user_edited: false,
    version: 0,
  };
}

export function FoodEditorModal(props: FoodEditorModalProps): JSX.Element {
  const {
    isOpen,
    initial,
    initialName,
    onDismiss,
    onSaved,
    onRequestArchive,
    onRequestUomRebase,
    detergentMode = false,
    lockDefaultUom = false,
  } = props;
  const { profile } = useAuth();
  const createMaster = useEnqueueMaster();
  const { data: recipes = [] } = useMasters<RecipeForCount>('recipes');

  const [form, setForm] = useState<InternalFormState>(() => buildInitialState(initial, initialName, detergentMode));
  const [editorStep, setEditorStep] = useState<FoodEditorStep>('identity');
  // URL firmato della foto GIÀ salvata (photo_file_id). Senza questo la sezione
  // Foto mostrava la dropzone vuota anche quando una foto era salvata, perché il
  // render guardava solo `photo_b64` (cattura nuova) e mai `photo_path` (salvata).
  const [savedPhotoUrl, setSavedPhotoUrl] = useState<string | null>(null);
  const nameRef = useRef<HTMLIonInputElement>(null);
  const [presentToast] = useIonToast();
  const setToast = (msg: string) => {
    void presentToast({ message: msg, duration: 1800, position: 'top' });
  };
  const celebrate = useCelebrate();

  // Reset dello stato ogni volta che il modal si riapre (cambio initial).
  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialState(initial, initialName, detergentMode));
      setEditorStep('identity');
    }
  }, [isOpen, initial, initialName, detergentMode]);

  // Carica l'URL firmato della foto salvata (photo_file_id) quando l'editor si
  // apre su un prodotto esistente e non c'è una nuova cattura in corso. 15 min.
  useEffect(() => {
    let cancelled = false;
    if (isOpen && form.photo_path && !form.photo_b64) {
      void getSupabase().storage
        .from('lot-photos')
        .createSignedUrl(form.photo_path, 60 * 15)
        .then(({ data }) => { if (!cancelled) setSavedPhotoUrl(data?.signedUrl ?? null); })
        .catch(() => { if (!cancelled) setSavedPhotoUrl(null); });
    } else if (!form.photo_path) {
      setSavedPhotoUrl(null);
    }
    return () => { cancelled = true; };
  }, [isOpen, form.photo_path, form.photo_b64]);

  // Counter ricette in cui questo food è ingrediente (cross-reference badge B10).
  const recipesUsingThisFood = useMemo(() => {
    if (!form.id) return 0;
    let n = 0;
    for (const r of recipes) {
      if (r.archived_at) continue;
      // T-441: non contare le varianti dieta (ricette figlie) — sono la stessa base.
      if (r.variant_of_recipe_id != null) continue;
      if ((r.ingredients ?? []).some((ing) => ing?.food_id === form.id)) n += 1;
    }
    return n;
  }, [recipes, form.id]);

  // Cambio del tipo di conservazione → ricarica il range target HACCP standard.
  function changeStorageType(next: string): void {
    const preset = STORAGE_TYPE_TEMP[next];
    setForm((f) => ({
      ...f,
      storage_type: next,
      target_temp_c_min: preset ? String(preset.min) : f.target_temp_c_min,
      target_temp_c_max: preset ? String(preset.max) : f.target_temp_c_max,
    }));
  }

  async function captureFoodPhoto(source: CaptureSource): Promise<void> {
    try {
      const photo = await getCaptureProvider().takePhoto(source);
      if (photo) setForm((f) => ({ ...f, photo_b64: photo.base64 }));
    } catch (err) {
      setToast(toUserMessage(err, 'Cattura annullata'));
    }
  }

  async function scanFoodBarcode(): Promise<void> {
    try {
      // Future: integrare @capacitor-mlkit/barcode-scanning.
      const photo = await getCaptureProvider().takePhoto('camera');
      if (photo) setToast('Scanner barcode non ancora attivo: digita il codice');
    } catch (err) {
      setToast(toUserMessage(err, 'Scanner annullato'));
    }
  }

  async function save(): Promise<void> {
    const canCreate = props.creationSource === 'quick_label'
      ? canCreateQuickLabelFood(profile)
      : canCreateFood(profile);
    if (!initial && !canCreate) {
      setToast('Il tuo livello di accesso può associare alimenti esistenti, ma non crearne di nuovi.');
      return;
    }
    if (!profile?.active_tenant_id) {
      setToast('Nessun tenant attivo — riavvia l\'app o rifai login');
      return;
    }
    const name = form.name.trim();
    if (!name) { setToast('Nome richiesto'); return; }
    if (
      form.id
      && lockDefaultUom
      && form.default_uom.trim().toLowerCase()
        !== (initial?.default_uom ?? '').trim().toLowerCase()
    ) {
      setToast('Per cambiare unità usa “Correggi unità” dalla giacenza: convertirà anche quantità e costo.');
      return;
    }
    const id = form.id || crypto.randomUUID();
    const isUpdate = !!form.id;
    const tMin = form.target_temp_c_min === '' ? null : Number(form.target_temp_c_min);
    const tMax = form.target_temp_c_max === '' ? null : Number(form.target_temp_c_max);
    const shelfLifeDaysOpened = form.shelf_life_days_opened === ''
      ? null
      : Number(form.shelf_life_days_opened);
    const shelfLifeHoursThawed = form.shelf_life_hours_thawed === ''
      ? null
      : Number(form.shelf_life_hours_thawed);
    if (tMin != null && !Number.isFinite(tMin)) { setToast('Temp. min non valida'); return; }
    if (tMax != null && !Number.isFinite(tMax)) { setToast('Temp. max non valida'); return; }
    if (tMin != null && tMax != null && tMin > tMax) { setToast('Temp. min > max'); return; }
    if (shelfLifeDaysOpened != null && (!Number.isInteger(shelfLifeDaysOpened) || shelfLifeDaysOpened <= 0)) {
      setToast('Shelf life per apertura: inserisci un numero intero di giorni');
      return;
    }
    if (shelfLifeHoursThawed != null && (!Number.isInteger(shelfLifeHoursThawed) || shelfLifeHoursThawed <= 0)) {
      setToast('Shelf life per scongelamento: inserisci un numero intero di ore');
      return;
    }
    try {
      let photoPath: string | null = form.photo_path;
      if (form.photo_b64) {
        try {
          const bytes = Uint8Array.from(atob(form.photo_b64), (c) => c.charCodeAt(0));
          const path = `${profile.active_tenant_id}/foods/${id}.jpg`;
          const { error } = await getSupabase().storage
            .from('lot-photos')
            .upload(path, bytes, { contentType: 'image/jpeg', upsert: true });
          if (error) throw error;
          photoPath = path;
        } catch (err) {
          setToast(`Foto non caricata: ${toUserMessage(err)}`);
        }
      }
      // T-459: unisce/carica la scheda tecnica (PDF) prima della persistenza.
      // In caso di errore fallback al path esistente (come la foto).
      let documentPath: string | null = form.techSheet.path;
      try {
        documentPath = await commitTechSheet('foods', profile.active_tenant_id, id, form.techSheet);
      } catch (err) {
        setToast(`${detergentMode ? 'Scheda di sicurezza' : 'Scheda tecnica'} non caricata: ${toUserMessage(err)}`);
      }
      // Allergeni: usa quanto editato dall'utente; se non ha mai toccato i toggle
      // (allergens_user_edited=false) fa un'ultima auto-suggerita dal nome+categoria.
      const allergensFinal = form.allergens_user_edited
        ? form.allergens
        : suggestAllergens(name, form.category || 'altro');
      await createMaster.mutateAsync({
        table: 'foods', id, op: isUpdate ? 'update' : 'insert', base_version: form.version ?? 0,
        patch: {
          id, tenant_id: profile.active_tenant_id, name,
          ...(!isUpdate && props.creationSource ? { creation_source: props.creationSource } : {}),
          label_alias: form.label_alias.trim() || null,
          default_uom: form.default_uom || 'kg',
          category: form.category || 'altro',
          storage_type: form.storage_type || 'fresco',
          target_temp_c_min: tMin,
          target_temp_c_max: tMax,
          shelf_life_days_opened: shelfLifeDaysOpened,
          shelf_life_hours_thawed: shelfLifeHoursThawed,
          barcode: form.barcode.trim() || null,
          photo_file_id: photoPath,
          ...(detergentMode
            ? { safety_sheet_file_id: documentPath }
            : { data_sheet_file_id: documentPath }),
          allergens: allergensFinal,
        },
      });
      celebrate(isUpdate ? 'Prodotto aggiornato' : 'Prodotto aggiunto');
      onSaved?.({
        id,
        name,
        label_alias: form.label_alias.trim() || null,
        default_uom: form.default_uom || 'kg',
        category: form.category || 'altro',
        storage_type: form.storage_type || 'fresco',
        target_temp_c_min: tMin,
        target_temp_c_max: tMax,
        shelf_life_days_opened: shelfLifeDaysOpened,
        shelf_life_hours_thawed: shelfLifeHoursThawed,
        barcode: form.barcode.trim() || null,
        photo_file_id: photoPath,
        ...(detergentMode
          ? { safety_sheet_file_id: documentPath }
          : { data_sheet_file_id: documentPath }),
        allergens: allergensFinal,
        version: (form.version ?? 0) + 1,
      });
      onDismiss();
    } catch (err: unknown) { setToast(toUserMessage(err)); }
  }

  return (
    <>
      <IonModal
        data-testid="food-editor"
        isOpen={isOpen}
        onDidDismiss={onDismiss}
        onDidPresent={() => focusInput(nameRef.current)}
      >
        <IonHeader><IonToolbar>
          <IonTitle>{form.id ? (detergentMode ? 'Modifica prodotto' : 'Modifica prodotto') : (detergentMode ? 'Nuovo prodotto' : 'Nuovo prodotto')}</IonTitle>
          <IonButtons slot="end"><IonButton onClick={onDismiss}><IonIcon icon={closeOutline} /></IonButton></IonButtons>
        </IonToolbar></IonHeader>
        <nav
          className="te-step-nav"
          aria-label="Sezioni prodotto"
          style={{ '--te-step-count': detergentMode ? 2 : 3 } as React.CSSProperties}
        >
          <button type="button" data-testid="food-step-identity" className={`te-step-nav__item ${editorStep === 'identity' ? 'is-active' : ''}`} onClick={() => setEditorStep('identity')}>
            <span>1</span>Dati base
          </button>
          {!detergentMode && (
            <button type="button" data-testid="food-step-safety" className={`te-step-nav__item ${editorStep === 'safety' ? 'is-active' : ''}`} onClick={() => setEditorStep('safety')}>
              <span>2</span>Conservazione
            </button>
          )}
          <button type="button" data-testid="food-step-details" className={`te-step-nav__item ${editorStep === 'details' ? 'is-active' : ''}`} onClick={() => setEditorStep('details')}>
            <span>{detergentMode ? '2' : '3'}</span>Documenti
          </button>
        </nav>
        <IonContent className="te-modal-page">
          <div className="te-flow-shell">
            {editorStep === 'identity' && (
            <div className="te-form-step">
            {/* === SEZIONE 1: IDENTIFICAZIONE === */}
            <h2 style={{ margin: '4px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              📦 Identificazione
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem
                lines="full"
                className={form.name.trim() ? 'te-field-ok' : 'te-field-missing'}
                style={{ '--background': form.name.trim() ? 'transparent' : '#fffbeb' } as React.CSSProperties}
              >
                <IonLabel position="stacked">Nome *</IonLabel>
                <IonInput
                  ref={nameRef}
                  value={form.name}
                  placeholder="Pollo intero"
                  onIonInput={(ev) => setForm((f) => {
                    const name = String(ev.detail.value ?? '');
                    return f.allergens_user_edited
                      ? { ...f, name }
                      : { ...f, name, allergens: suggestAllergens(name, f.category) };
                  })}
                />
              </IonItem>
              {!detergentMode && (
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Nome in etichetta (corto)</IonLabel>
                <IonInput
                  value={form.label_alias}
                  placeholder={form.name.trim() ? `es. accorcialo per l'etichetta` : 'es. Olio d\'oliva EV'}
                  maxlength={60}
                  onIonInput={(ev) => setForm((f) => ({ ...f, label_alias: String(ev.detail.value ?? '') }))}
                />
                <IonNote slot="helper" style={{ fontSize: 11 }}>
                  Nome breve stampato nella lista ingredienti delle etichette (se vuoto usa il nome completo). Il nome pieno resta nel magazzino e nella tracciabilità.
                </IonNote>
              </IonItem>
              )}
              {!detergentMode && (
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Categoria</IonLabel>
                <IonSelect
                  interface="action-sheet"
                  value={form.category}
                  onIonChange={(ev) => setForm((f) => {
                    const category = String(ev.detail.value);
                    return f.allergens_user_edited
                      ? { ...f, category }
                      : { ...f, category, allergens: suggestAllergens(f.name, category) };
                  })}
                >
                  {FOOD_CATEGORIES.map((c) => (
                    <IonSelectOption key={c.value} value={c.value}>{c.label}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              )}
              {/* T-290: set UdM canonico completo (pz/kg/g/l/ml/conf/ct/cassa/
                  bottiglia) + personalizzate, via componente condiviso. Prima
                  offriva solo 5 voci → mancavano conf/ct/cassa/bottiglia. */}
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <UomSelect
                  label="Unità di misura"
                  value={form.default_uom}
                  disabled={!!form.id && lockDefaultUom}
                  onChange={(u) => setForm((f) => ({ ...f, default_uom: u }))}
                />
                {!!form.id && lockDefaultUom && (
                  <IonNote slot="helper" className="ion-text-wrap" style={{ fontSize: 11 }}>
                    Per modificarla usa “Correggi unità” dalla giacenza: il sistema convertirà insieme quantità e costo.
                  </IonNote>
                )}
              </IonItem>
              {!!form.id && lockDefaultUom && onRequestUomRebase && (
                <IonButton
                  fill="outline"
                  size="small"
                  onClick={() => onRequestUomRebase(form.id)}
                  style={{ margin: '8px 4px 0', textTransform: 'none' }}
                >
                  Correggi unità e giacenza
                </IonButton>
              )}
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Barcode (EAN-13 / UPC)</IonLabel>
                <IonInput
                  value={form.barcode}
                  placeholder="es. 8001234567890"
                  onIonInput={(ev) => setForm((f) => ({ ...f, barcode: String(ev.detail.value ?? '') }))}
                />
                <IonButton slot="end" fill="clear" size="small" onClick={() => { void scanFoodBarcode(); }} style={{ '--padding-start': '8px', '--padding-end': '8px' } as React.CSSProperties}>
                  <IonIcon icon={scanOutline} slot="icon-only" />
                </IonButton>
              </IonItem>
            </div>
            {form.id && recipesUsingThisFood > 0 && (
              <div style={{
                marginTop: 16, padding: '10px 14px',
                background: '#eff6ff', borderRadius: 10,
                color: '#0066cc', fontSize: 12, fontWeight: 600,
              }}>
                Utilizzato in {recipesUsingThisFood} ricett{recipesUsingThisFood === 1 ? 'a' : 'e'}
              </div>
            )}
            </div>
            )}

            {!detergentMode && editorStep === 'safety' && (<div className="te-form-step">
            {/* === SEZIONE 2: CONSERVAZIONE === */}
            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🌡️ Conservazione
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              {/* Chip strip per tipo conservazione (più immediato di un IonSelect) */}
              <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                Tipo
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {([
                  { v: 'fresco',    label: 'Fresco',    color: '#0ea5e9', range: '0–4°' },
                  { v: 'surgelato', label: 'Surgelato', color: '#6366f1', range: '-25 / -18°' },
                  { v: 'secco',     label: 'Secco',     color: '#f59e0b', range: '10–25°' },
                  { v: 'bevanda',   label: 'Bevanda',   color: '#10b981', range: '4–16°' },
                ] as const).map((s) => {
                  const active = form.storage_type === s.v;
                  return (
                    <button
                      key={s.v}
                      type="button"
                      onClick={() => changeStorageType(s.v)}
                      style={{
                        flex: '1 1 auto',
                        background: active ? s.color : '#eef2f7',
                        color: active ? '#fff' : '#475569',
                        border: `1px solid ${active ? s.color : '#e2e8f0'}`,
                        borderRadius: 10,
                        padding: '8px 6px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                        boxShadow: active ? `0 1px 3px ${s.color}33` : 'none',
                      }}
                    >
                      <span>{s.label}</span>
                      <span style={{ fontSize: 10, opacity: active ? 0.85 : 0.6, fontVariantNumeric: 'tabular-nums' }}>{s.range}</span>
                    </button>
                  );
                })}
              </div>
              {/* Range temperatura editabile */}
              <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                Temp. target (°C)
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <IonInput
                  type="number"
                  inputmode="decimal"
                  placeholder="min"
                  value={form.target_temp_c_min}
                  onIonInput={(ev) => setForm((f) => ({ ...f, target_temp_c_min: String(ev.detail.value ?? '') }))}
                  style={{
                    flex: 1, fontSize: 14, fontVariantNumeric: 'tabular-nums',
                    '--padding-start': '10px', '--padding-end': '10px',
                    '--padding-top': '8px', '--padding-bottom': '8px',
                    '--background': '#eef2f7', '--border-radius': '10px',
                  } as React.CSSProperties}
                />
                <span style={{ color: '#94a3b8', fontSize: 14 }}>—</span>
                <IonInput
                  type="number"
                  inputmode="decimal"
                  placeholder="max"
                  value={form.target_temp_c_max}
                  onIonInput={(ev) => setForm((f) => ({ ...f, target_temp_c_max: String(ev.detail.value ?? '') }))}
                  style={{
                    flex: 1, fontSize: 14, fontVariantNumeric: 'tabular-nums',
                    '--padding-start': '10px', '--padding-end': '10px',
                    '--padding-top': '8px', '--padding-bottom': '8px',
                    '--background': '#eef2f7', '--border-radius': '10px',
                  } as React.CSSProperties}
                />
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, fontStyle: 'italic' }}>
                Pre-compilato sul tipo conservazione. Modificabile.
              </div>
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  {form.storage_type === 'surgelato'
                    ? 'Shelf life per scongelamento'
                    : 'Shelf life per apertura confezione'}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <IonInput
                    type="number"
                    inputmode="numeric"
                    min="1"
                    step="1"
                    placeholder={form.storage_type === 'surgelato' ? 'es. 48' : 'es. 3'}
                    value={form.storage_type === 'surgelato'
                      ? form.shelf_life_hours_thawed
                      : form.shelf_life_days_opened}
                    onIonInput={(ev) => {
                      const value = String(ev.detail.value ?? '');
                      setForm((f) => form.storage_type === 'surgelato'
                        ? { ...f, shelf_life_hours_thawed: value }
                        : { ...f, shelf_life_days_opened: value });
                    }}
                    style={{
                      flex: 1, fontSize: 14, fontVariantNumeric: 'tabular-nums',
                      '--padding-start': '10px', '--padding-end': '10px',
                      '--padding-top': '8px', '--padding-bottom': '8px',
                      '--background': '#eef2f7', '--border-radius': '10px',
                    } as React.CSSProperties}
                  />
                  <span style={{ color: '#475569', fontSize: 13, minWidth: 42 }}>
                    {form.storage_type === 'surgelato' ? 'ore' : 'giorni'}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                  Usata automaticamente per calcolare la scadenza dell’etichetta.
                  Se cambi conservazione, compare il valore pertinente senza cancellare l’altro.
                </div>
              </div>
            </div>
            {/* === SEZIONE 4: ALLERGENI (Reg. UE 1169/2011) === */}
            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Allergeni {form.allergens_user_edited ? '' : '(suggeriti)'}
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(Object.keys(ALLERGEN_LABELS_IT) as Allergen[]).map((a) => {
                  const active = form.allergens.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setForm((f) => {
                        const next = active
                          ? f.allergens.filter((x) => x !== a)
                          : [...f.allergens, a];
                        return { ...f, allergens: next, allergens_user_edited: true };
                      })}
                      style={{
                        border: `1px solid ${active ? '#0066cc' : '#cbd5e1'}`,
                        background: active ? '#0066cc' : '#fff',
                        color: active ? '#fff' : '#475569',
                        borderRadius: 999,
                        padding: '5px 11px',
                        fontSize: 12,
                        fontWeight: active ? 600 : 500,
                        cursor: 'pointer',
                      }}
                    >
                      {ALLERGEN_LABELS_IT[a]}
                    </button>
                  );
                })}
              </div>
              {!form.allergens_user_edited && form.allergens.length > 0 && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, fontStyle: 'italic' }}>
                  Suggeriti in base a nome e categoria. Tocca per confermare o modificare.
                </div>
              )}
            </div>
            </div>)}

            {editorStep === 'details' && (
            <div className="te-form-step">
            {/* === SEZIONE 3: FOTO === */}
            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              📷 Foto
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              {(form.photo_b64 || savedPhotoUrl) ? (
                <div style={{ position: 'relative' }}>
                  <img
                    src={form.photo_b64 ? `data:image/jpeg;base64,${form.photo_b64}` : (savedPhotoUrl ?? '')}
                    alt="Foto prodotto"
                    style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 10 }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <IonButton size="small" fill="outline" onClick={() => { void captureFoodPhoto('camera'); }} style={{ '--border-radius': '10px', flex: 1 } as React.CSSProperties}>
                      <IonIcon icon={cameraOutline} slot="start" />
                      Sostituisci
                    </IonButton>
                    <IonButton size="small" fill="clear" color="danger" onClick={() => { setSavedPhotoUrl(null); setForm((f) => ({ ...f, photo_b64: null, photo_path: null })); }} style={{ '--border-radius': '10px' } as React.CSSProperties}>
                      <IonIcon icon={closeOutline} slot="icon-only" />
                    </IonButton>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { void captureFoodPhoto('camera'); }}
                  style={{
                    width: '100%', padding: '24px 16px',
                    background: '#eef2f7', border: '2px dashed #cbd5e1', borderRadius: 12,
                    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                    color: '#64748b',
                  }}
                >
                  <IonIcon icon={cameraOutline} style={{ fontSize: 32, color: '#94a3b8' }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Scatta una foto</span>
                  <span style={{ fontSize: 11 }}>Aiuta lo staff a riconoscere il prodotto</span>
                </button>
              )}
            </div>

            {/* === SEZIONE SCHEDA TECNICA (T-459) — anche per i detergenti === */}
            <TechSheetPicker
              state={form.techSheet}
              onChange={(ts) => setForm((f) => ({ ...f, techSheet: ts }))}
              title={detergentMode ? '📄 Scheda di sicurezza (SDS)' : undefined}
            />
            </div>
            )}

            {editorStep === 'details' && form.id && onRequestArchive && (
              <IonButton
                expand="block"
                color="danger"
                fill="clear"
                style={{ '--border-radius': '12px', marginTop: 8 } as React.CSSProperties}
                onClick={() => {
                  onRequestArchive({ id: form.id, name: form.name, version: form.version });
                  onDismiss();
                }}
              >
                <IonIcon icon={trashOutline} slot="start" />
                Archivia prodotto
              </IonButton>
            )}

            {/* === AZIONI === */}
            <div className="te-action-dock">
              {editorStep !== 'identity' && (
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={() => setEditorStep(
                    editorStep === 'details' && !detergentMode ? 'safety' : 'identity'
                  )}
                  style={{ flex: 1 }}
                >
                  Indietro
                </IonButton>
              )}
              {editorStep !== 'details' ? (
                <IonButton
                  expand="block"
                  onClick={() => setEditorStep(editorStep === 'identity' && !detergentMode ? 'safety' : 'details')}
                  style={{ flex: 2 }}
                >
                  Continua
                </IonButton>
              ) : (
              <IonButton
                expand="block"
                onClick={() => void save()}
                style={{ flex: 2 }}
              >
                {form.id ? 'Salva modifiche' : (detergentMode ? 'Crea prodotto' : 'Crea prodotto')}
              </IonButton>
              )}
            </div>
          </div>
        </IonContent>
      </IonModal>
    </>
  );
}
